import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { Farm, FarmLocation, FarmMachine, FarmCosts, FarmReview, FarmShift } from '../types';

export interface FarmDetailData {
  farm:            Farm;
  location:        FarmLocation | null;
  machines:        FarmMachine[];
  materials:       string[];
  specializations: string[];
  costs:           FarmCosts | null;
  shift:           FarmShift | null;
  reviews:         FarmReview[];
  avg_rating:      number;
  reviews_count:   number;
  /** máquinas reservadas por modelo, chave `brand|||model` */
  reservations:    Record<string, number>;
}

export function useFarm(slug: string) {
  const [data, setData]       = useState<FarmDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setError('');

    // Fase 1: busca farm pelo slug
    supabase.from('farms').select('*').eq('slug', slug).single().then(({ data: farmRow, error: fErr }) => {
      if (fErr || !farmRow) {
        setError('Farm não encontrada.');
        setLoading(false);
        return;
      }
      const farm = farmRow as unknown as Farm;
      const farmId = farm.id;

      // Fase 2: busca dados relacionados em paralelo com WHERE correto
      Promise.all([
        supabase.from('farm_locations').select('*').eq('farm_id', farmId).maybeSingle(),
        supabase.from('farm_machines').select('*').eq('farm_id', farmId),
        supabase.from('farm_materials').select('material').eq('farm_id', farmId),
        supabase.from('farm_specializations').select('specialization').eq('farm_id', farmId),
        supabase.from('farm_costs').select('*').eq('farm_id', farmId).maybeSingle(),
        supabase.from('farm_reviews').select('id, farm_id, farm_order_id, reviewer_user_id, rating, comment, created_at').eq('farm_id', farmId).order('created_at', { ascending: false }),
        supabase.from('farm_shifts').select('*').eq('farm_id', farmId).maybeSingle(),
        supabase.rpc('farm_machine_reservations' as never, { p_farm_id: farmId } as never),
      ]).then(([locRes, machRes, matRes, specRes, costRes, revRes, shiftRes, resvRes]) => {
        const location        = (locRes.data as unknown as FarmLocation | null) ?? null;
        const machines        = (machRes.data as unknown as FarmMachine[]) ?? [];
        const materials       = ((matRes.data ?? []) as unknown as { material: string }[]).map((m) => m.material);
        const specializations = ((specRes.data ?? []) as unknown as { specialization: string }[]).map((s) => s.specialization);
        const costs           = (costRes.data as unknown as FarmCosts | null) ?? null;
        const reviews         = (revRes.data as unknown as FarmReview[]) ?? [];
        const shift           = (shiftRes.data as unknown as FarmShift | null) ?? null;
        const avg_rating      = reviews.length ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length : 0;

        const reservations: Record<string, number> = {};
        for (const row of ((resvRes.data ?? []) as unknown as { machine_brand: string; machine_model: string; reserved: number }[])) {
          reservations[`${row.machine_brand}|||${row.machine_model}`] = row.reserved;
        }

        setData({ farm, location, machines, materials, specializations, costs, shift, reviews, avg_rating, reviews_count: reviews.length, reservations });
        setLoading(false);
      });
    });
  }, [slug]);

  return { data, loading, error };
}
